import { SwishIcon } from '@/components/swish-icon';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Label } from '@/features/label/components/label';
import { LabelGroup } from '@/features/label/types';
import { PrintWrapper } from '@/features/misc/components/print-wrapper';
import { useOrganization } from '@/features/organizer/api/getOrganization';
import { usePaymentConfirmation } from '@/features/payment/api/get-payment-confirmation';
import { PaymentIcon } from '@/features/payment/components/payment-icon';
import { PayType } from '@/features/payment/types';
import { convertUTCtoLocal } from '@/utils/convertUtcToLocal';
import { Fragment } from 'react/jsx-runtime';

type PaymentDialogProps = {
  paymentId: number;
};

export function PaymentDialog({ paymentId }: PaymentDialogProps) {
  const { data: confirmation, isLoading, isFetched } = usePaymentConfirmation({ paymentId, config: { enabled: !!paymentId } });

  const { data: organization } = useOrganization({
    organizerId: confirmation?.payment?.organizerId ?? 0,
    config: { enabled: isFetched && !!confirmation?.payment?.organizerId }
  });

  const total = confirmation?.orderRows?.reduce((acc, or) => (acc += or.amount + or.vatAmount), 0);
  const vatAmount = confirmation?.orderRows?.reduce((acc, or) => (acc += or.vatAmount), 0);

  const payDate = confirmation?.payment?.payDate && convertUTCtoLocal(confirmation?.payment?.payDate?.toString());

  const paidWith = (() => {
    switch (confirmation?.payment?.payType) {
      case PayType.Card:
        return 'card';
      case PayType.Invoice:
        return 'invoice';
      case PayType.Swish:
        return 'swish';
      default:
        return '';
    }
  })();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="hover:scale-110 transition-transform">
          <PaymentIcon payType={confirmation?.payment?.payType ?? PayType.Other} />
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader />

        {isLoading ? (
          <div className="flex justify-center items-center h-48">
            <Spinner />
          </div>
        ) : (
          <PrintWrapper>
            <Card style={{ overflow: 'hidden', border: 'none', boxShadow: 'none' }}>
              <CardHeader style={{ marginBottom: '0.5rem' }}>
                <CardTitle
                  style={{
                    fontWeight: 600,
                    fontSize: '1.875rem',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                  <h2 style={{ fontWeight: 600 }}>
                    {confirmation?.payment?.payType === PayType.Card || confirmation?.payment?.payType === PayType.Swish ? <Label name="receipt" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} /> : <Label name="invoice_basis" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />}
                  </h2>
                </CardTitle>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.875rem'
                  }}>
                  <p
                    style={{
                      fontWeight: 600,
                      color: 'var(--muted-foreground)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    {payDate}
                    <span
                      style={{
                        fontStyle: 'italic',
                        color: 'var(--muted-foreground)'
                      }}>
                      <Label name="payment_id" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} /> <strong>{confirmation?.payment?.paymentId}</strong>
                    </span>
                  </p>

                  <p
                    style={{
                      fontWeight: 600,
                      color: 'var(--muted-foreground)',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                    <Label name={`paid_with_${paidWith}`} groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                    {confirmation?.payment?.payType === PayType.Swish && (
                      <SwishIcon
                        style={{
                          width: '5rem',
                          height: '5rem',
                          marginRight: '0.5rem'
                        }}
                      />
                    )}
                  </p>
                </div>
              </CardHeader>

              <Separator />

              <CardContent>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    color: 'var(--muted-foreground)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}>
                  <div style={{ gridColumn: 'span 3', padding: '1rem 0' }}>
                    <Label name="event" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                  </div>
                  <div
                    style={{
                      gridColumn: 'span 1',
                      padding: '1rem 0',
                      textAlign: 'right'
                    }}>
                    <Label name="quantity" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                  </div>
                  <div
                    style={{
                      gridColumn: 'span 1',
                      padding: '1rem 0',
                      textAlign: 'right'
                    }}>
                    <Label name="priceExVat" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                  </div>
                  <div
                    style={{
                      gridColumn: 'span 1',
                      padding: '1rem 0',
                      textAlign: 'right'
                    }}>
                    <Label name="priceIncVat" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                  </div>
                </div>

                <Separator />

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                  {confirmation?.orderRows?.map(or => (
                    <Fragment key={or.orderRowId}>
                      <div
                        style={{
                          gridColumn: 'span 3',
                          padding: '1rem 0'
                        }}>
                        {or.name}
                      </div>
                      <div
                        style={{
                          gridColumn: 'span 1',
                          padding: '1rem 0',
                          textAlign: 'right'
                        }}>
                        1
                      </div>
                      <div
                        style={{
                          gridColumn: 'span 1',
                          padding: '1rem 0',
                          textAlign: 'right'
                        }}>
                        {or.amount}
                      </div>
                      <div
                        style={{
                          gridColumn: 'span 1',
                          padding: '1rem 0',
                          textAlign: 'right'
                        }}>
                        {or.amount + or.vatAmount}
                      </div>
                    </Fragment>
                  ))}
                </div>
              </CardContent>

              <Separator />

              <CardFooter
                style={{
                  display: 'grid',
                  gridTemplateColumns: window.innerWidth > 768 ? 'repeat(6, 1fr)' : 'repeat(3, 1fr)',
                  color: 'var(--muted-foreground)',
                  gap: '2rem',
                  alignItems: 'flex-start'
                }}>
                <div
                  style={{
                    gridColumn: 'span 3',
                    padding: '1rem 0'
                  }}>
                  {confirmation?.payment.payType === PayType.Invoice && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        fontSize: '0.875rem'
                      }}>
                      <span
                        style={{
                          textTransform: 'uppercase',
                          fontWeight: 600
                        }}>
                        <Label name="invoice_details" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                      </span>

                      <Separator />

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                        <span>{organization?.invoiceName}</span>
                        <span>{organization?.invoiceOrgNo}</span>
                        <span>
                          {organization?.invoiceAddress1}, {organization?.invoiceZip}, {organization?.invoiceCity}
                        </span>
                        <span>{organization?.invoiceEmail}</span>
                        <span>{organization?.invoiceRef}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    gridColumn: 'span 3'
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '1rem 0'
                    }}>
                    <p
                      style={{
                        color: 'var(--muted-foreground)',
                        fontSize: '0.75rem'
                      }}>
                      <Label name="subtotal" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                    </p>
                    <p>
                      {(total ?? 0) - (vatAmount ?? 0)}
                      SEK
                    </p>
                  </div>
                  <Separator />

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '1rem 0'
                    }}>
                    <p
                      style={{
                        color: 'var(--muted-foreground)',
                        fontSize: '0.75rem'
                      }}>
                      <Label name="vat" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                    </p>
                    <p>{vatAmount} SEK</p>
                  </div>

                  <Separator />

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem 0'
                    }}>
                    <p
                      style={{
                        color: 'var(--muted-foreground)'
                      }}>
                      <Label name="total" groupName={LabelGroup.ORGANIZER_PAYMENT_RECEIPT} />
                    </p>
                    <p
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: 700,
                        color: 'var(--primary)'
                      }}>
                      {total} SEK
                    </p>
                  </div>
                </div>
              </CardFooter>
            </Card>
          </PrintWrapper>
        )}
      </DialogContent>
    </Dialog>
  );
}
